import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  QuizAttempt,
  QuizAnswer,
  Question,
  QuizResult,
  QuizGenerationRequest,
  QuizStatus,
  QuizContextType
} from '../types';
import {
  generateQuiz,
  getQuizAttempt,
  submitQuizAnswer,
  completeQuizAttempt,
  resumeQuizAttempt,
  abandonQuizAttempt,
  getIncompleteQuizzes,
} from '../services/quizService';
import { useUser } from './UserContext';

const QuizContext = createContext<QuizContextType | undefined>(undefined);
export { QuizContext };

/**
 * Quiz Provider that manages quiz attempts using the database-integrated system
 * Supports dynamic quiz generation, resumption, and real-time progress tracking
 */
export const QuizProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get user authentication context
  const { currentUser, isLoading: userLoading } = useUser();

  // Core quiz state
  const [currentAttempt, setCurrentAttempt] = useState<QuizAttempt | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [results, setResults] = useState<QuizResult | null>(null);

  // Progress state
  const [quizProgress, setQuizProgress] = useState({
    totalQuestions: 0,
    answeredQuestions: 0,
    correctAnswers: 0,
    progressPercentage: 0,
    currentQuestionOrder: 0
  });

  // Loading and error state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Clears any existing error state
   */
  const clearError = () => setError(null);

  /**
   * Updates the progress tracking based on current answers
   */
  const updateProgress = useCallback(() => {
    if (!currentAttempt) return;

    const answered = answers.filter(a => a.userAnswer !== undefined && a.userAnswer !== null).length;
    const correct = answers.filter(a => a.isCorrect === true).length;
    const percentage = currentAttempt.totalQuestions > 0 ? (answered / currentAttempt.totalQuestions) * 100 : 0;

    setQuizProgress({
      totalQuestions: currentAttempt.totalQuestions,
      answeredQuestions: answered,
      correctAnswers: correct,
      progressPercentage: percentage,
      currentQuestionOrder: currentQuestionIndex + 1
    });
  }, [currentAttempt, answers, currentQuestionIndex]);

  /**
   * Generates a new quiz based on the provided request parameters
   */
  const generateNewQuiz = useCallback(async (request: QuizGenerationRequest): Promise<string> => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if user authentication is still loading
      if (userLoading) {
        throw new Error('Please wait for authentication to complete before generating a quiz.');
      }

      // Check if user is authenticated
      if (!currentUser) {
        console.error('❌ No authenticated user found');
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('You must be logged in to generate a quiz. Please log in and try again.');
        } else {
          // Token exists but user is null - likely invalid/expired token
          console.error('❌ Token exists but user is null - clearing token and requesting fresh login');
          localStorage.removeItem('token');
          throw new Error('Your session has expired. Please log in again to continue.');
        }
      }

      const response = await generateQuiz(request);

      // Set up the quiz attempt state using the authenticated user
      const attempt: QuizAttempt = {
        attemptId: response.attemptId,
        userId: currentUser.userId,
        status: response.status,
        difficulty: 'easy', // This would come from the response
        attemptNumber: 1,
        totalQuestions: response.totalQuestions,
        correctAnswers: 0,
        startedAt: new Date(),
        ...(request.startFrom === 'content_point' && { startedFromContentPointId: request.nodeId }),
        ...(request.startFrom === 'outcome' && { startedFromOutcomeId: request.nodeId })
      };

      setCurrentAttempt(attempt);
      setQuestions(response.questions);

      // Initialize answers array
      const initialAnswers: QuizAnswer[] = response.questions.map((question, index) => ({
        answerId: 0, // Will be set by backend
        attemptId: response.attemptId,
        questionId: question.questionId,
        questionOrder: index + 1,
        userAnswer: undefined,
        isCorrect: undefined,
        answeredAt: undefined,
        question
      }));

      setAnswers(initialAnswers);
      setCurrentQuestionIndex(0);
      setResults(null);

      return response.attemptId;
    } catch (err) {
      console.error('❌ Quiz generation failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate quiz';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, userLoading]);

  /**
   * Loads an existing quiz attempt
   */
  const loadQuizAttempt = useCallback(async (attemptId: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const attemptData = await getQuizAttempt(attemptId);

      setCurrentAttempt(attemptData);
      setQuestions(attemptData.questions || []);
      setAnswers(attemptData.answers || []);

      // Find current question index based on first unanswered question or last question
      const answers = attemptData.answers || [];
      const firstUnanswered = answers.findIndex(a => !a.userAnswer);
      setCurrentQuestionIndex(firstUnanswered >= 0 ? firstUnanswered : Math.max(0, answers.length - 1));

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load quiz attempt';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Resumes an incomplete quiz attempt
   */
  const resumeQuiz = useCallback(async (attemptId: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const attemptData = await resumeQuizAttempt(attemptId);

      setCurrentAttempt(attemptData);
      setQuestions(attemptData.questions || []);
      setAnswers(attemptData.answers || []);

      // Find current question index
      const answers = attemptData.answers || [];
      const firstUnanswered = answers.findIndex(a => !a.userAnswer);
      setCurrentQuestionIndex(firstUnanswered >= 0 ? firstUnanswered : 0);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resume quiz';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Abandons the current quiz attempt
   */
  const abandonCurrentQuiz = useCallback(async (): Promise<void> => {
    if (!currentAttempt) return;

    // Prevent duplicate abandon requests
    if (isLoading) {
      console.warn('Abandon request already in progress, ignoring duplicate request');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await abandonQuizAttempt(currentAttempt.attemptId);

      // Clear current state
      setCurrentAttempt(null);
      setQuestions([]);
      setAnswers([]);
      setCurrentQuestionIndex(0);
      setResults(null);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to abandon quiz';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAttempt, isLoading]);

  /**
   * Abandons a specific quiz attempt by ID (without affecting current quiz state)
   */
  const abandonSpecificQuiz = useCallback(async (attemptId: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      await abandonQuizAttempt(attemptId);

      // Only clear current quiz state if this was the current quiz
      if (currentAttempt && currentAttempt.attemptId === attemptId) {
        setCurrentAttempt(null);
        setQuestions([]);
        setAnswers([]);
        setCurrentQuestionIndex(0);
        setResults(null);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to abandon quiz';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAttempt]);

  /**
   * Navigation functions
   */
  const moveToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const moveToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const jumpToQuestion = (index: number) => {
    if (index >= 0 && index < questions.length) {
      setCurrentQuestionIndex(index);
    }
  };

  /**
   * Submits an answer for the current question
   */
  const submitAnswer = useCallback(async (userAnswer: string): Promise<void> => {
    if (!currentAttempt || !questions[currentQuestionIndex]) return;

    try {
      setIsLoading(true);
      setError(null);

      const currentQuestion = questions[currentQuestionIndex];

      // Find the corresponding answer record to get the correct questionOrder
      const currentAnswer = answers.find(answer => answer.questionId === currentQuestion.questionId);
      if (!currentAnswer) {
        throw new Error('Answer record not found for current question');
      }

      const updatedAnswer = await submitQuizAnswer(
        currentAttempt.attemptId,
        currentQuestion.questionId,
        userAnswer,
        currentAnswer.questionOrder
      );

      // Update local answers state
      setAnswers(prev => prev.map(answer =>
        answer.questionId === currentQuestion.questionId
          ? { ...answer, ...updatedAnswer }
          : answer
      ));

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit answer';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAttempt, questions, currentQuestionIndex]);

  /**
   * Gets the current answer for the current question
   */
  const getCurrentAnswer = useCallback((): string | undefined => {
    if (!questions[currentQuestionIndex]) return undefined;

    const currentQuestionId = questions[currentQuestionIndex].questionId;
    const answer = answers.find(a => a.questionId === currentQuestionId);
    return answer?.userAnswer;
  }, [questions, currentQuestionIndex, answers]);

  /**
   * Completes the current quiz and gets results
   */
  const completeQuiz = useCallback(async (): Promise<QuizResult | null> => {
    if (!currentAttempt) return null;

    try {
      setIsLoading(true);
      setError(null);

      const quizResult = await completeQuizAttempt(currentAttempt.attemptId);
      setResults(quizResult);

      // Update attempt status
      setCurrentAttempt(prev => prev ? { ...prev, status: 'completed' as QuizStatus, completedAt: new Date() } : null);

      return quizResult;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete quiz';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAttempt]);

  /**
   * Gets incomplete quiz attempts for the authenticated user
   */
  const getIncompleteAttempts = useCallback(async (userId?: number): Promise<QuizAttempt[]> => {
    try {
      // Use authenticated user if no userId provided
      const targetUserId = userId || currentUser?.userId;

      if (!targetUserId) {
        throw new Error('No user ID available for fetching incomplete quizzes');
      }

      return await getIncompleteQuizzes(targetUserId);
    } catch (err) {
      console.error('Failed to fetch incomplete quizzes:', err);
      setError('Failed to load incomplete quizzes');
      return [];
    }
  }, [currentUser]);

  // Computed properties
  const isQuizCompleted = currentAttempt?.status === 'completed';
  const hasUnansweredQuestions = answers && answers.length > 0 ? answers.some(a => !a.userAnswer) : false;

  // Update progress whenever relevant state changes
  React.useEffect(() => {
    updateProgress();
  }, [currentAttempt, answers, currentQuestionIndex, updateProgress]);

  return (
    <QuizContext.Provider value={{
      // State
      currentAttempt,
      questions,
      answers,
      currentQuestionIndex,

      // Quiz management
      generateNewQuiz,
      loadQuizAttempt,
      resumeQuiz,
      abandonCurrentQuiz,
      abandonSpecificQuiz,

      // Navigation
      moveToNextQuestion,
      moveToPreviousQuestion,
      jumpToQuestion,

      // Answer submission
      submitAnswer,
      getCurrentAnswer,

      // Completion
      completeQuiz,
      results,

      // Progress
      quizProgress,

      // Utility
      getIncompleteAttempts,
      isQuizCompleted,
      hasUnansweredQuestions,

      // Loading state
      isLoading,
      error,
      clearError
    }}>
      {children}
    </QuizContext.Provider>
  );
};

/**
 * Custom hook to use the quiz context
 */
export const useQuiz = (): QuizContextType => {
  const context = useContext(QuizContext);
  if (context === undefined) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
};